import React, { useState, useEffect, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import "./App.css";
import { GoogleOAuthProvider } from '@react-oauth/google';

// Auth Context
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { BrandingProvider, useBranding } from "./contexts/BrandingContext";

// Components
import Navigation from "./components/Navigation";
import Dashboard from "./components/Dashboard";
import Setup from "./components/Setup";

import KPIs from "./components/KPIs";
import PlanningDashboard from "./components/PlanningDashboard";
import FinancialHealthDashboard from "./components/FinancialHealthDashboard";
import PerformanceAnalytics from "./components/PerformanceAnalytics";
import PlanningDetails from "./components/PlanningDetails";
import ForecastingScenarios from "./components/ForecastingScenarios";
import StrategicFramework from "./components/StrategicFramework";
import CostManagementV2 from "./components/CostManagementV2";
import FinancialManagementV2 from "./components/FinancialManagementV2";
import FinancialPlanningV2 from "./components/FinancialPlanningV2";
import AdvancedCalculations from "./components/AdvancedCalculations";
import EnhancedFinancialCalculations from "./components/EnhancedFinancialCalculations";
import EnhancedCheckout from "./components/cart/EnhancedCheckout";
import CartAnalytics from "./components/cart/CartAnalytics";
import FinancialStatements from "./components/FinancialStatements";
import BusinessPlanList from "./components/BusinessPlanList";
import BusinessPlanListPage from "./components/BusinessPlanListPage";
import AuthPage from "./components/auth/AuthPage";
import AdminDashboard from "./components/admin/AdminDashboard";
import PlatformAdminDashboard from "./components/admin/PlatformAdminDashboard";
import WhiteLabelManager from "./components/admin/WhiteLabelManager";
import PlatformManager from "./components/admin/PlatformManager";
import AffiliateTierManagement from "./components/admin/AffiliateTierManagement";
import AdminPayoutManagement from "./components/admin/AdminPayoutManagement";
import PlanReportsExplorer from "./components/export/PlanReportsExplorer";
import PaymentPage from "./components/payments/PaymentPage";
import PricingPage from "./components/pricing/PricingPage";
import QuoteCalculator from "./components/pricing/QuoteCalculator";

// Enterprise Components - Phase 3
import SubsidiaryManager from "./components/enterprise/SubsidiaryManager";
import RoleManagement from "./components/admin/RoleManagement";

// Bank Edition Components - WF2-WF12 (All Phases)
import TenantManagement from "./components/bank/TenantManagement";
import OrganizationHierarchy from "./components/bank/OrganizationHierarchy";
import ReviewQueue from "./components/bank/ReviewQueue";
import ReviewDetails from "./components/bank/ReviewDetails";
import ApprovalQueue from "./components/bank/ApprovalQueue";
import TemplateSelector from "./components/bank/TemplateSelector";
import UserManagement from "./components/bank/UserManagement";
import GuidedPlanBuilder from "./components/bank/GuidedPlanBuilder";
import MarketAnalysisForm from "./components/bank/MarketAnalysisForm";
import RiskRegisterForm from "./components/bank/RiskRegisterForm";
import ActualsEntryForm from "./components/bank/ActualsEntryForm";
import FinancialProjections from "./components/bank/FinancialProjections";
import BudgetTargetSetting from "./components/bank/BudgetTargetSetting";
import ConsolidationDashboard from "./components/bank/ConsolidationDashboard";
import VarianceAnalysis from "./components/bank/VarianceAnalysis";
import TargetCascade from "./components/bank/TargetCascade";
import CreditPackGenerator from "./components/bank/CreditPackGenerator";
import EvidencePackDownloader from "./components/bank/EvidencePackDownloader";
import ExportJobTracker from "./components/bank/ExportJobTracker";
import VersionHistory from "./components/bank/VersionHistory";
import PlanLockUnlock from "./components/bank/PlanLockUnlock";
import StatusTimeline from "./components/bank/StatusTimeline";
import CompanySettings from "./components/company/CompanySettings";
import SecuritySettings from "./components/auth/SecuritySettings";
// Scenario Modeling Component
import ScenarioModeling from "./components/ScenarioModeling";

// Affiliate System Components
import AffiliateDashboard from "./components/affiliate/AffiliateDashboard";
import AffiliateRegistration from "./components/affiliate/AffiliateRegistration";
import AffiliateSignup from "./components/affiliate/AffiliateSignup";
import AffiliatePayouts from "./components/affiliate/AffiliatePayouts";
import AffiliateManagement from "./components/admin/AffiliateManagement";

// Auth Components
import Signup from "./components/auth/Signup";
import ForgotPassword from "./components/auth/ForgotPassword";
import ResetPassword from "./components/auth/ResetPassword";
import VerifyEmail from "./components/auth/VerifyEmail";
import AcceptInvitation from "./components/auth/AcceptInvitation";
import VerificationPending from "./components/auth/VerificationPending";

// Add-on Services Components
import AddOnCatalog from "./components/addons/AddOnCatalog";
import ShoppingCart from "./components/addons/ShoppingCart";
import MyAddOns from "./components/addons/MyAddOns";
import MySubscription from "./components/subscription/MySubscription";
import AddOnManagement from "./components/admin/AddOnManagement";
import FeatureGovernance from "./components/admin/FeatureGovernance";
import OrderConfirmation from "./components/cart/OrderConfirmation";

// Performance Tracking Components - NEW Clean Architecture
import SalesActualsEntry from "./components/performance/SalesActualsEntry";
import CostActualsEntry from "./components/performance/CostActualsEntry";
import PerformanceExecutiveDashboard from "./components/performance/ExecutiveDashboard";
import RevenueAnalysis from "./components/performance/RevenueAnalysis";
import CostAnalysis from "./components/performance/CostAnalysis";
import ProfitabilityAnalysis from "./components/performance/ProfitabilityAnalysis";
import StrategicAlignment from "./components/performance/StrategicAlignment";
import VarianceAnalytics from "./components/performance/VarianceAnalytics";
import TrendsForecasting from "./components/performance/TrendsForecasting";
import DetailedReports from "./components/performance/DetailedReports";

// Phase 6D: Cost Center Management
import CostCenterManagement from "./components/CostCenterManagement";

// Report Templates System
import ReportCenter from "./components/reports/ReportCenter";
import TemplateBuilder from "./components/reports/TemplateBuilder";
import ReportGeneratePage from "./components/reports/ReportGeneratePage";

// Toaster
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Context for sharing business plan data
const BusinessPlanContext = React.createContext();

export const useBusinessPlan = () => {
  const context = React.useContext(BusinessPlanContext);
  if (!context) {
    throw new Error('useBusinessPlan must be used within a BusinessPlanProvider');
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading TavPlans...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AuthPage />;
  }

  return children;
};

// Feature Protected Route Component - enforces subscription tier restrictions
import FeatureGate from './components/FeatureGate';

const FeatureProtectedRoute = ({ feature, children }) => {
  return (
    <FeatureGate feature={feature} showUpgradeModal={true}>
      {children}
    </FeatureGate>
  );
};

import { getUserType } from './utils/userType';
import { useCurrency } from './contexts/CurrencyContext';

// Main App Content Component
const AppContent = () => {
  const { user, isAuthenticated } = useAuth();
  const { updatePlanBaseCurrency, updateDisplayCurrency } = useCurrency();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeScenario, setActiveScenario] = useState("realistic");
  const [isAffiliateOnly, setIsAffiliateOnly] = useState(false);
  const [userType, setUserType] = useState('new'); // 'affiliateOnly', 'hybrid', 'businessOnly', 'new'
  const location = useLocation();

  // Helper function to save selected plan to localStorage
  const saveSelectedPlanId = (planId) => {
    if (planId && user?.id) {
      localStorage.setItem(`selectedPlan_${user.id}`, planId);
    }
  };

  // Helper function to get saved plan ID from localStorage
  const getSavedPlanId = () => {
    if (user?.id) {
      return localStorage.getItem(`selectedPlan_${user.id}`);
    }
    return null;
  };

  // Wrapper for setCurrentPlan that also persists to localStorage
  const updateCurrentPlan = (plan) => {
    setCurrentPlan(plan);
    if (plan?.id) {
      saveSelectedPlanId(plan.id);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      // Check user type and existing data on startup
      const checkUserTypeAndPlans = async () => {
        try {
          // SUPERADMIN CHECK - Admins bypass restrictions but still load their data
          if (user?.is_admin === true) {
            console.log('SUPERADMIN detected - bypassing restrictions');
            setUserType('businessOnly');
            
            // Still load admin's business plans if they exist
            try {
              const token = Cookies.get('enakid_token');
              const response = await axios.get(`${API}/business-plans`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              console.log('Admin business plans found:', response.data.length);
              if (response.data.length > 0) {
                // Check if there's a saved plan preference
                const savedPlanId = getSavedPlanId();
                const savedPlan = savedPlanId 
                  ? response.data.find(p => p.id === savedPlanId)
                  : null;
                
                // Use saved plan if found, otherwise use first plan
                const planToSet = savedPlan || response.data[0];
                updateCurrentPlan(planToSet);
                console.log('Selected plan:', planToSet.name);
              }
            } catch (planError) {
              console.log('Admin has no business plans (optional for admins)');
            }
            
            setLoading(false);
            return; // Exit after loading admin data
          }
          
          // Determine user type (for non-admins)
          const type = await getUserType();
          console.log('User type detected:', type);
          setUserType(type);
          
          // If user is affiliate-only, skip business plan requirement
          if (type === 'affiliateOnly') {
            setIsAffiliateOnly(true);
            setLoading(false);
            return;
          }
          
          // For all other types, check for business plans
          // (hybrid, businessOnly, or even 'new' - someone might have a plan)
          try {
            const token = Cookies.get('enakid_token');
            const response = await axios.get(`${API}/business-plans`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            console.log('Business plans found:', response.data.length);
            if (response.data.length > 0) {
              // Check if there's a saved plan preference
              const savedPlanId = getSavedPlanId();
              const savedPlan = savedPlanId 
                ? response.data.find(p => p.id === savedPlanId)
                : null;
              
              // Use saved plan if found, otherwise use first plan
              const planToSet = savedPlan || response.data[0];
              updateCurrentPlan(planToSet);
              console.log('Selected plan:', planToSet.name);
            }
          } catch (planError) {
            console.error('Error fetching business plans:', planError);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setLoading(false);
        }
      };

      checkUserTypeAndPlans();
    }
  }, [isAuthenticated, user]);

  // Sync currency context when current plan changes
  useEffect(() => {
    if (currentPlan && currentPlan.currency) {
      // Set both plan base currency and display currency from the plan
      updatePlanBaseCurrency(currentPlan.currency);
      updateDisplayCurrency(currentPlan.currency);
      console.log(`[CURRENCY] Synced with plan currency: ${currentPlan.currency}`);
    }
  }, [currentPlan, updatePlanBaseCurrency, updateDisplayCurrency]);

  // Function to refresh user type (called when affiliate creates business plan)
  const refreshUserType = async () => {
    const type = await getUserType();
    setUserType(type);
    if (type !== 'affiliateOnly') {
      setIsAffiliateOnly(false);
    }
  };

  const createNewPlan = async (planData) => {
    try {
      const response = await axios.post(`${API}/business-plans`, planData);
      updateCurrentPlan(response.data);
      
      // Create sample data for the new plan
      await axios.post(`${API}/sample-data/${response.data.id}`);
      
      return response.data;
    } catch (error) {
      console.error("Error creating business plan:", error);
      throw error;
    }
  };

  // Routes that don't require a business plan
  const routesWithoutPlan = ['/enhanced-export', '/signup', '/pricing', '/affiliate/signup', '/join-affiliate', '/affiliate/register', '/affiliate', '/affiliate/dashboard', '/company-settings', '/subsidiaries', '/reports'];
  const currentPath = location.pathname;
  
  // Check if current path matches any route that doesn't require a plan
  // Supports both exact matches and path prefixes (for dynamic routes like /reports/builder/:id)
  const isPathWithoutPlan = routesWithoutPlan.some(route => currentPath === route) || 
                            currentPath.startsWith('/reports/');
  
  // Admin users bypass all business plan requirements
  const isAdmin = user?.is_admin === true;
  const requiresPlan = !isPathWithoutPlan && !isAffiliateOnly && !isAdmin;
  
  console.log('App.js State Check:', {
    currentPath,
    isAdmin,
    isAffiliateOnly,
    userType,
    currentPlan: currentPlan?.id || 'none',
    requiresPlan,
    loading
  });

  if (loading && requiresPlan) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your business plans...</p>
        </div>
      </div>
    );
  }

  if (!currentPlan && requiresPlan && !isAffiliateOnly && !isAdmin) {
    return (
      <BusinessPlanContext.Provider value={{ 
        currentPlan, 
        setCurrentPlan: updateCurrentPlan, 
        createNewPlan, 
        activeScenario, 
        setActiveScenario,
        user,
        userType,
        refreshUserType
      }}>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
          <BusinessPlanList />
          <Toaster />
        </div>
      </BusinessPlanContext.Provider>
    );
  }

  return (
    <BusinessPlanContext.Provider value={{ 
      currentPlan, 
      setCurrentPlan: updateCurrentPlan, 
      createNewPlan, 
      activeScenario, 
      setActiveScenario,
      user,
      userType,
      refreshUserType
    }}>
      <Routes>
        {/* Authenticated Routes */}
        <Route path="/*" element={
          <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
            <div className="flex">
              <Navigation userType={userType} refreshUserType={refreshUserType} userTypeLoading={loading} />
              <main className="flex-1 ml-64">
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/business-plans" element={<BusinessPlanListPage />} />
              <Route path="/setup" element={<FeatureProtectedRoute feature="setup"><Setup /></FeatureProtectedRoute>} />
              <Route path="/financial-statements" element={<FinancialStatements />} />
              <Route path="/kpis" element={<FeatureProtectedRoute feature="kpis"><KPIs /></FeatureProtectedRoute>} />
              <Route path="/planning-dashboard" element={<PlanningDashboard />} />
              <Route path="/financial-health" element={<FinancialHealthDashboard />} />
              <Route path="/performance-analytics" element={<PerformanceAnalytics />} />
              <Route path="/planning-details" element={<PlanningDetails />} />
              <Route path="/forecasting-scenarios" element={<ForecastingScenarios />} />
              <Route path="/strategic-framework" element={<FeatureProtectedRoute feature="strategicframework"><StrategicFramework /></FeatureProtectedRoute>} />
              <Route path="/cost-management" element={<FeatureProtectedRoute feature="costmanagementv2"><CostManagementV2 /></FeatureProtectedRoute>} />
              <Route path="/financial-management" element={<FeatureProtectedRoute feature="financialmanagementv2"><FinancialManagementV2 /></FeatureProtectedRoute>} />
              <Route path="/financial-planning" element={<FinancialPlanningV2 />} />
              <Route path="/advanced-calculations" element={<AdvancedCalculations />} />
              <Route path="/enhanced-financial-calculations" element={<FeatureProtectedRoute feature="enhancedfinancialcalculations"><EnhancedFinancialCalculations /></FeatureProtectedRoute>} />
              <Route path="/enhanced-checkout" element={<EnhancedCheckout />} />
              <Route path="/cart-analytics" element={<CartAnalytics />} />
              <Route path="/scenario-modeling" element={<FeatureProtectedRoute feature="scenariomodeling"><ScenarioModeling /></FeatureProtectedRoute>} />
              <Route path="/cost-centers" element={<FeatureProtectedRoute feature="costcentermanagement"><CostCenterManagement /></FeatureProtectedRoute>} />
              <Route path="/enhanced-export" element={<PlanReportsExplorer />} />
              
              {/* Report Templates System */}
              <Route path="/reports" element={<ReportCenter />} />
              <Route path="/reports/builder" element={<TemplateBuilder />} />
              <Route path="/reports/builder/:templateId" element={<TemplateBuilder />} />
              <Route path="/reports/generate/:templateId" element={<ReportGeneratePage />} />
              <Route path="/reports/preview/:templateId" element={<ReportGeneratePage />} />
              
              {/* Company Settings Routes */}
              <Route path="/company-settings" element={<CompanySettings />} />
              <Route path="/security" element={<SecuritySettings />} />
              <Route path="/subsidiaries" element={<SubsidiaryManager />} />
              
              {/* Performance Tracking Routes */}
              <Route path="/executive-dashboard" element={<PerformanceExecutiveDashboard />} />
              <Route path="/revenue-analysis" element={<RevenueAnalysis />} />
              <Route path="/cost-analysis" element={<CostAnalysis />} />
              <Route path="/profitability-analysis" element={<ProfitabilityAnalysis />} />
              <Route path="/strategic-alignment" element={<StrategicAlignment />} />
              <Route path="/variance-analytics" element={<VarianceAnalytics />} />
              <Route path="/trends-forecasting" element={<TrendsForecasting />} />
              <Route path="/detailed-reports" element={<DetailedReports />} />
              <Route path="/sales-actuals-entry" element={<SalesActualsEntry />} />
              <Route path="/cost-actuals-entry" element={<CostActualsEntry />} />
              
              {/* Payment Routes */}
              <Route path="/payment" element={<PaymentPage />} />
              <Route path="/checkout" element={<PaymentPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/quote-calculator" element={<QuoteCalculator />} />

              {/* Admin Routes */}
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/platform-management" element={<PlatformAdminDashboard />} />
              <Route path="/admin/roles" element={<FeatureProtectedRoute feature="rolemanagement"><RoleManagement /></FeatureProtectedRoute>} />
              <Route path="/admin/feature-governance" element={<FeatureGovernance />} />
              <Route path="/admin/platform" element={<PlatformManager />} />
              <Route path="/admin/whitelabel" element={<WhiteLabelManager />} />
              <Route path="/admin/affiliates" element={<AffiliateManagement />} />
              <Route path="/admin/affiliate-tiers" element={<AffiliateTierManagement />} />
              <Route path="/admin/payouts" element={<AdminPayoutManagement />} />
              <Route path="/admin/add-ons" element={<AddOnManagement />} />

              {/* Bank Edition Routes - Complete (All Phases 1-5) */}
              <Route path="/bank/tenants" element={<TenantManagement />} />
              <Route path="/bank/organizations" element={<OrganizationHierarchy />} />
              <Route path="/bank/users" element={<UserManagement />} />
              <Route path="/bank/review-queue" element={<ReviewQueue />} />
              <Route path="/bank/review/:reviewId" element={<ReviewDetails />} />
              <Route path="/bank/approval-queue" element={<ApprovalQueue />} />
              <Route path="/bank/templates" element={<TemplateSelector />} />
              <Route path="/bank/plan/create" element={<GuidedPlanBuilder />} />
              <Route path="/bank/market-analysis/:planId" element={<MarketAnalysisForm />} />
              <Route path="/bank/risk-register/:planId" element={<RiskRegisterForm />} />
              <Route path="/bank/actuals/:planId" element={<ActualsEntryForm />} />
              <Route path="/bank/financials/:planId" element={<FinancialProjections />} />
              <Route path="/bank/budget-targets" element={<BudgetTargetSetting />} />
              <Route path="/bank/cascade" element={<TargetCascade />} />
              <Route path="/bank/consolidation" element={<ConsolidationDashboard />} />
              <Route path="/bank/variance" element={<VarianceAnalysis />} />
              <Route path="/bank/credit-pack" element={<CreditPackGenerator />} />
              <Route path="/bank/evidence-pack" element={<EvidencePackDownloader />} />
              <Route path="/bank/export-jobs" element={<ExportJobTracker />} />
              <Route path="/bank/version-history/:planId" element={<VersionHistory />} />
              <Route path="/bank/plan-lock/:planId" element={<PlanLockUnlock />} />
              <Route path="/bank/status-timeline/:planId" element={<StatusTimeline />} />

              {/* Add-on Services Routes */}
              <Route path="/add-ons" element={<AddOnCatalog />} />
              <Route path="/cart" element={<ShoppingCart />} />
              <Route path="/my-add-ons" element={<MyAddOns />} />
              <Route path="/subscription" element={<MySubscription />} />
              <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
              <Route path="/order-confirmation" element={<OrderConfirmation />} />

              {/* Affiliate Routes */}
              <Route path="/affiliate" element={<AffiliateDashboard />} />
              <Route path="/affiliate/register" element={<AffiliateRegistration />} />
              <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
              <Route path="/affiliate/payouts" element={<AffiliatePayouts />} />
              
              {/* TODO: Re-enable admin check after testing
              {user?.is_admin && (
                <>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/roles" element={<RoleManagement />} />
                  <Route path="/admin/feature-governance" element={<FeatureGovernance />} />
                  <Route path="/admin/platform" element={<PlatformManager />} />
                  <Route path="/admin/whitelabel" element={<WhiteLabelManager />} />
                </>
              )}
              */}
                </Routes>
              </main>
            </div>
            <Toaster />
          </div>
        } />
      </Routes>
    </BusinessPlanContext.Provider>
  );
};

function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  
  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <AuthProvider>
        <SubscriptionProvider>
          <CurrencyProvider>
            <BrandingProvider>
              <BrowserRouter>
                <AffiliateTracker />
                <Toaster />
                <Routes>
                  {/* Public Routes - No authentication required */}
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/quote-calculator" element={<QuoteCalculator />} />
                  <Route path="/affiliate/signup" element={<AffiliateSignup />} />
                  <Route path="/join-affiliate" element={<AffiliateSignup />} />
                  
                  {/* Auth Routes - Password Reset & Email Verification */}
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/verification-pending" element={<VerificationPending />} />
                  <Route path="/invitation/accept" element={<AcceptInvitation />} />
                  
                  {/* Protected routes */}
                  <Route path="/*" element={
                    <ProtectedRoute>
                      <AppContent />
                    </ProtectedRoute>
                  } />
                </Routes>
              </BrowserRouter>
            </BrandingProvider>
          </CurrencyProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

// Affiliate click tracking component - runs on ALL routes
const AffiliateTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const refCode = searchParams.get('ref');
    
    if (refCode) {
      console.log(`🔗 Affiliate link detected: ref=${refCode}`);
      
      // Store ref code in localStorage for later conversion tracking
      localStorage.setItem('affiliate_ref', refCode);
      console.log('💾 Stored affiliate ref in localStorage');
      
      // Track the click
      const trackingUrl = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/affiliates/track-click?affiliate_code=${refCode}&target_url=${encodeURIComponent(window.location.href)}`;
      
      console.log('📊 Tracking click to:', trackingUrl);
      
      fetch(trackingUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
        .then(response => {
          if (response.ok) {
            console.log('✅ Affiliate click tracked successfully for:', refCode);
            return response.json();
          } else {
            console.error('❌ Tracking failed with status:', response.status);
            throw new Error('Tracking failed');
          }
        })
        .then(data => {
          console.log('📈 Tracking response:', data);
        })
        .catch(err => {
          console.error('❌ Affiliate tracking error:', err);
        });
    }
  }, [location]);
  
  return null; // This component doesn't render anything
};

export default App;